import React, { useEffect, useState } from "react";
import "../css/Homepage.css";
import { useNavigate } from "react-router-dom";
import Header from "./Header";

const Homepage = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleNavigate = (category) => {
    navigate(`/question-hub/${category}`);
  };

  // 添加滚动到STEM学科区域的函数
  const scrollToSubjects = () => {
    const subjectsSection = document.querySelector('.subjects');
    if (subjectsSection) {
      subjectsSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const subjects = [
    {
      name: "Science",
      icon: "🔬",
      description: "Explore the wonders of natural phenomena",
      gradient: "from-blue-400 to-purple-600",
      category: "science"
    },
    {
      name: "Technology",
      icon: "💻",
      description: "Dive into digital innovation and computing",
      gradient: "from-green-400 to-cyan-500",
      category: "technology"
    },
    {
      name: "Engineering",
      icon: "⚙️",
      description: "Build and design solutions for tomorrow",
      gradient: "from-orange-400 to-red-500",
      category: "engineering"
    },
    {
      name: "Math",
      icon: "📐",
      description: "Master the language of the universe",
      gradient: "from-purple-400 to-pink-500",
      category: "math"
    }
  ];

  const features = [
    {
      icon: "📚",
      title: "SPM Past Year Question",
      description: "Access actual exam questions to sharpen your understanding and prepare with confidence"

    },
    {
      icon: "📊",
      title: "Progress Analytics",
      description: "Detailed insights into your learning patterns and skill development"
    },
    {
      icon: "🌐",
      title: "Borderless Education",
      description: "Breaking geographical barriers with 24/7 global access to premium STEM education"
    },
    {
      icon: "🚀",
      title: "Instant Precision",
      description: "Lightning-fast responses powered by cutting-edge AI technology"
    },
    {
      icon: "🎨",
      title: "Visual Learning",
      description: "Interactive diagrams, animations, and visual explanations for complex concepts"
    },
    {
      icon: "🏆",
      title: "Achievement System",
      description: "Gamified learning with badges, milestones, and competitive elements"
    }
  ];

  const testimonials = [
    {
      text: "AskTeach AI transformed my understanding of complex physics. The instant explanations are incredible!",
      author: "Sarah Chen",
      role: "Physics Student",
      location: "Rural Malaysia"
    },
    {
      text: "Finally, an AI that explains calculus in a way that actually makes sense. Game-changer for rural students!",
      author: "Ahmad Rahman",
      role: "Engineering Student",
      location: "Remote Indonesia"
    },
    {
      text: "The 24/7 availability means I can learn at my own pace. No more waiting for office hours!",
      author: "Maria Santos",
      role: "Computer Science Student",
      location: "Philippines"
    }
  ];

  const achievements = [
    {
      icon: "🌟",
      number: "50K+",
      label: "Questions Solved",
      description: "Across all STEM subjects"
    },
    {
      icon: "👥",
      number: "15K+",
      label: "Active Learners",
      description: "From 20+ countries"
    },
    {
      icon: "⚡",
      number: "2.3s",
      label: "Avg Response Time",
      description: "Lightning-fast AI processing"
    },
    {
      icon: "🎯",
      number: "96%",
      label: "Success Rate",
      description: "Student satisfaction score"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="homepage">
      <Header />
      
      {/* Enhanced background elements */}
      <div className="bg-elements">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        <div className="floating-shape shape-5"></div>
        <div className="floating-shape shape-6"></div>
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Enhanced Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-icon">✨</span>
            <span>Next-Generation AI Learning Platform</span>
          </div>
          <h1 className="hero-title">
            Unlock <span className="highlight">limitless potential</span>
            <br />
            with AI-powered <span className="highlight-2">STEM mastery</span>
          </h1>
          <p className="hero-subtitle">
            Experience the future of education with AskTeach AI - where artificial intelligence 
            meets personalized learning to democratize premium STEM education for every student, 
            everywhere.
          </p>
          <div className="hero-cta">
            <button className="cta-primary" onClick={scrollToSubjects}>
              Start Learning Now
            </button>
            <button className="cta-secondary">
              <span className="play-icon">▶</span>
              Watch Demo
            </button>
          </div>
        </div>
        <div className="hero-visual">
          <div className="floating-elements">
            <div className="element element-1">🔬</div>
            <div className="element element-2">💻</div>
            <div className="element element-3">⚙️</div>
            <div className="element element-4">📐</div>
            <div className="element element-5">🧮</div>
            <div className="element element-6">🔭</div>
          </div>
        </div>
      </section>

      {/* Achievement Stats */}
      <section className="achievements">
        <div className="achievements-grid">
          {achievements.map((achievement, index) => (
            <div 
              key={index}
              className="achievement-card"
              style={{ 
                animationDelay: `${index * 0.1}s`,
                opacity: mounted ? 1 : 0
              }}
            >
              <div className="achievement-icon">{achievement.icon}</div>
              <div className="achievement-number">{achievement.number}</div>
              <div className="achievement-label">{achievement.label}</div>
              <div className="achievement-description">{achievement.description}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Subjects Section - Keep original structure */}
      <section className="subjects">
        <div className="subjects-header">
          <div className="section-badge">
            <span>🎓</span>
            <span>STEM Universe</span>
          </div>
          <h2>Choose Your Learning Adventure</h2>
          <p>Embark on a personalized journey through the fascinating world of STEM subjects</p>
        </div>
        
        <div className="subject-grid">
          {subjects.map((subject, index) => (
            <div
              key={subject.category}
              className={`subject-card ${subject.category}`}
              onClick={() => handleNavigate(subject.category)}
              style={{ 
                animationDelay: `${index * 0.1}s`,
                opacity: mounted ? 1 : 0
              }}
            >
              <div className="card-content">
                <div className="card-icon">{subject.icon}</div>
                <h3 className="card-title">{subject.name}</h3>
                <p className="card-description">{subject.description}</p>
                <div className="card-arrow">→</div>
              </div>
              <div className="card-glow"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="features">
        <div className="features-header">
          <div className="section-badge">
            <span>⚡</span>
            <span>AI-Powered Features</span>
          </div>
          <h2>Why Students Choose AskTeach AI</h2>
          <p>Discover the revolutionary features that make learning effortless and engaging</p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => {
            const handleClick = () => {
              if (feature.title === "SPM Past Year Question") {
                navigate("/past-year");
              } else if (feature.title === "Progress Analytics") {
                navigate("/AnalysisDashboard");
              }
            };

            return (
              <div
                key={index}
                className={`feature-card ${
                  feature.title === "Adaptive Intelligence" || feature.title === "Progress Analytics"
                    ? "clickable"
                    : ""
                }`}
                style={{
                  animationDelay: `${(index + 1) * 0.1}s`,
                  opacity: mounted ? 1 : 0
                }}
                onClick={
                  feature.title === "SPM Past Year Question" || feature.title === "Progress Analytics"
                    ? handleClick
                    : null
                }
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            );
          })}
        </div>

      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="testimonials-header">
          <div className="section-badge">
            <span>💬</span>
            <span>Student Stories</span>
          </div>
          <h2>Transforming Lives Through AI Education</h2>
          <p>Real stories from students who've accelerated their learning journey with AskTeach AI</p>
        </div>
        <div className="testimonial-carousel">
          <div className="testimonial-card">
            <div className="testimonial-content">
              <div className="quote-icon">"</div>
              <p className="testimonial-text">{testimonials[currentTestimonial].text}</p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>{testimonials[currentTestimonial].author}</h4>
                  <p>{testimonials[currentTestimonial].role}</p>
                  <span className="location">{testimonials[currentTestimonial].location}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button 
                key={index}
                className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                onClick={() => setCurrentTestimonial(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Transform Your STEM Journey?</h2>
          <p>Join thousands of students who've unlocked their potential with AI-powered learning</p>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={scrollToSubjects}>
              Get Started Free
            </button>
            <button className="cta-secondary">Explore Demo</button>
          </div>
        </div>
        <div className="cta-visual">
          <div className="success-metrics">
            <div className="metric">
              <span className="metric-icon">📈</span>
              <span className="metric-text">87% Grade Improvement</span>
            </div>
            <div className="metric">
              <span className="metric-icon">⏱️</span>
              <span className="metric-text">50% Faster Learning</span>
            </div>
            <div className="metric">
              <span className="metric-icon">🎯</span>
              <span className="metric-text">95% Concept Retention</span>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-logo">
              <div className="logo-text">
                <span className="logo-name">AskTeach AI</span>
                <span className="logo-tagline">Empowering Rural STEM Education</span>
              </div>
            </div>
            <div className="footer-description">
              <p>Bridging the education gap with cutting-edge AI technology, making premium STEM education accessible to every student, everywhere.</p>
            </div>
          </div>
          <div className="footer-links-grid">
            <div className="link-group">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#demo">Demo</a>
            </div>
            <div className="link-group">
              <h4>Resources</h4>
              <a href="#blog">Blog</a>
              <a href="#help">Help Center</a>
              <a href="#api">API Docs</a>
            </div>
            <div className="link-group">
              <h4>Company</h4>
              <a href="#about">About</a>
              <a href="#careers">Careers</a>
              <a href="#contact">Contact</a>
            </div>
            <div className="link-group">
              <h4>Legal</h4>
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
              <a href="#cookies">Cookies</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-social">
            <a href="#" className="social-link">Twitter</a>
            <a href="#" className="social-link">LinkedIn</a>
            <a href="#" className="social-link">GitHub</a>
          </div>
          <p>© 2025 AskTeach AI · Revolutionizing STEM Education with AI</p>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;